import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ChannelsCountLike } from '@common/utils/sales-channel-counts.util';
import { cloneProduct, setChannelValue } from '@common/utils/sales-channel-pricing.util';
import { BulkUpdateData } from '@modules/pos/models/bulk-update-data.model';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ToastService } from './toast.service';

export interface ProductForSalesChannels {
    _id: string;
    name: string;
    salesChannels: any;
    sellingPrice: number;
    /** Costo vigente según el método de costeo del comercio (FIFO o ponderado). Lo calcula la API, es solo lectura. */
    cost: number;
    updatedAt: Date;
    /** Línea «SKU · Unidad · Categoría» de la tarjeta mobile. Receta/Combo y Servicios no tienen SKU. */
    SKU?: string;
    /** Unidad tal como la guarda el producto («Und.», «Kg.»…). */
    units?: string;
    category?: string;
}

/** Cambio masivo pendiente de un canal. En memoria hay uno por canal; al guardar se agrupan (`groupBulkToggles`). */
export interface BulkChannelToggle {
    salesChannelId: string;
    enabled: boolean;
    productIds: string[];
}

/** Masivo de varios canales con la MISMA lista de productos: la API aplica `enabled` en cada canal a cada producto. */
export interface BulkMultiChannelToggle {
    salesChannelIds: string[];
    enabled: boolean;
    productIds: string[];
}

/** Cada elemento de `bulkToggles` tal como viaja a `update-products`. */
export type BulkTogglePayload = BulkChannelToggle | BulkMultiChannelToggle;

/**
 * Estado de un canal sobre todo el conjunto del filtro, tal como lo devuelve el listado. Es lo único
 * que necesita el checkbox de header: marcado, desmarcado o indeterminado.
 */
export type ChannelState = 'all' | 'none' | 'mixed';

/**
 * Índice ligero del catálogo que matchea el filtro vigente, ya normalizado para consulta directa:
 * `enabledByProduct` mapea cada producto al conjunto de canales que tiene habilitados.
 */
export interface ChannelIndex {
    productIds: string[];
    enabledByProduct: Map<string, Set<string>>;
    totalDocs: number;
    truncated: boolean;
}

interface ProductsSalesChannelsParams {
    currentPage?: number;
    pageSize?: number;
    search?: string;
    type?: string;
}

/**
 * Lista cargada en pantalla: la consulta con la que se pidió (sección, tipo y búsqueda) y hasta qué página llegó.
 * Las tandas siguientes se piden con ESTA consulta, no con lo que diga el buscador en ese momento: si el usuario
 * escribe y cancela el aviso de cambios sin guardar, la lista sigue siendo la de la búsqueda anterior.
 */
interface ListState {
    section: string;
    type: string;
    search: string;
    page: number;
    totalPages: number;
    totalDocs: number;
}

/** Productos por tanda: lo que pagina el servidor para esta pantalla. */
const PAGE_SIZE = 50;

/**
 * La API reescribe el mapa de canales completo de cada producto editado: un canal eliminado con ediciones pendientes
 * volvería a aparecer en esos productos. Se envían solo los canales que siguen en `liveChannelIds`.
 */
function withoutRemovedChannels(product: any, liveChannelIds: Set<string>): any {
    const channels: Record<string, any> = product?.salesChannels;
    if (!channels || Object.keys(channels).every(channelId => liveChannelIds.has(channelId))) return product;
    const kept = Object.keys(channels)
        .filter(channelId => liveChannelIds.has(channelId))
        .reduce((acc: Record<string, any>, channelId: string) => ({ ...acc, [channelId]: channels[channelId] }), {});
    return { ...product, salesChannels: kept };
}

/**
 * Arma los masivos tal como viajan al guardar. Los que llevan el mismo `enabled` y la misma lista de productos (el
 * «Activar todos» de mobile, o varios encabezados de desktop seguidos) van juntos en un solo toggle con
 * `salesChannelIds`: la API cuenta los ids una vez por toggle, y así 21 canales con todo el catálogo caben en su tope
 * y en el límite del cuerpo. Un grupo de un solo canal conserva la forma de siempre.
 *
 * Cada canal tiene un solo masivo, así que agruparlos no cambia el resultado aunque cambie el orden en que se aplican.
 *
 * Con `liveChannelIds` solo viajan los masivos de esos canales: el de un canal eliminado con el masivo pendiente
 * volvería a crear la entrada del canal en todos los productos del filtro (la API agrega las que faltan al activar). Un
 * grupo se arma solo con los canales que quedan, así que ninguno viaja sin canales.
 */
function groupBulkToggles(toggles: BulkChannelToggle[], liveChannelIds: Set<string> | null): BulkTogglePayload[] {
    const groups: BulkMultiChannelToggle[] = [];
    for (const toggle of toggles) {
        if (liveChannelIds && !liveChannelIds.has(toggle.salesChannelId)) continue;
        const group = groups.find(candidate => candidate.enabled === toggle.enabled && sameIds(candidate.productIds, toggle.productIds));
        if (group) group.salesChannelIds.push(toggle.salesChannelId);
        else groups.push({ salesChannelIds: [toggle.salesChannelId], enabled: toggle.enabled, productIds: toggle.productIds });
    }

    return groups.map(group => (group.salesChannelIds.length === 1 ? { salesChannelId: group.salesChannelIds[0], enabled: group.enabled, productIds: group.productIds } : group));
}

/** Misma lista de ids en el mismo orden: todas salen del mismo índice del filtro y las exclusiones no lo alteran. */
function sameIds(a: string[], b: string[]): boolean {
    if (a === b) return true;
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

@Injectable({
    providedIn: 'root',
})
export class SalesChannelsService implements OnDestroy {
    API_URL = environment.API_URL;
    salesChannels$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    private _itemsToUpdate$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private _templatesToUpdate$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    SCCopy$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    editedSalesChannels$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    items$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    templates$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    salesChannelsLoaded$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    selectedSection$: BehaviorSubject<string> = new BehaviorSubject<string>('inventory');

    // Productos
    products: ProductForSalesChannels[] = [];
    productsToUpdate: ProductForSalesChannels[] = [];

    /** Emite al terminar cada recarga desde el inicio (pestaña, búsqueda o un flujo externo). */
    productsLoaded$: Subject<void> = new Subject<void>();
    /** Emite al anexar una tanda, con las filas nuevas (puede venir vacía si todo estaba repetido). */
    productsAppended$: Subject<ProductForSalesChannels[]> = new Subject<ProductForSalesChannels[]>();
    /** Emite cuando cambian el estado o los contadores por canal sin recargar la lista (tras guardar). */
    channelsSummaryChanged$: Subject<void> = new Subject<void>();

    /**
     * Recarga desde el inicio en curso. Distinto de `loading$`, que también encienden otros flujos de la pantalla
     * (alta y borrado de canal, carga masiva): solo este oculta las filas.
     */
    listLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /** Tanda siguiente en curso. */
    loadingMore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /** La última tanda falló: la pantalla ofrece reintentar y deja de pedir sola. */
    loadMoreFailed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private list: ListState = null;
    /**
     * Cada recarga desde el inicio invalida las respuestas que estaban en vuelo: una tanda o un resumen que llega
     * después pertenece a otra lista y no se aplica.
     */
    private listGeneration = 0;
    private listRequest: Subscription = null;

    /** Cada producto tal como lo trajo el listado, para comparar y restaurar sin depender del orden de las filas. */
    private originalsById = new Map<string, ProductForSalesChannels>();

    /**
     * Estado de cada canal sobre todo el conjunto del filtro, que el listado devuelve junto con la
     * página. Permite pintar el checkbox de header correctamente desde la carga inicial, sin descargar
     * el índice.
     *
     * Se guarda junto con la sección que lo produjo: el estado de una sección no debe pintar los headers
     * de otra.
     */
    private channelsStateCache: { section: string; state: Record<string, ChannelState> } = null;

    /** Contadores por canal del listado (`channelsCount`), junto con la sección que los produjo. */
    private channelsCountCache: { section: string; counts: ChannelsCountLike } = null;

    /**
     * Sube con cada resumen pedido y con cada provisional: solo se aplica la respuesta del último pedido. Dos guardados
     * seguidos no deben dejar el resumen del primero (leído antes del segundo) encima del más reciente.
     */
    private summarySequence = 0;

    /**
     * Índice del filtro vigente, cacheado por (sección + término de búsqueda aplicado): al cambiar
     * cualquiera de los dos la clave deja de coincidir y el caché queda inválido solo. Cada recarga
     * desde el inicio lo invalida explícitamente, porque puede venir de otro flujo que modificó el catálogo.
     */
    private channelIndexCache: { key: string; index: ChannelIndex } = null;

    /** Cambios masivos pendientes, uno por canal. El último click sobre un canal reemplaza al anterior. */
    private bulkToggles: Map<string, BulkChannelToggle> = new Map<string, BulkChannelToggle>();

    readonly pageSize: number = PAGE_SIZE;

    // Búsqueda de cada sección
    searchControl = new FormControl('');
    searchControlTemplates = new FormControl('');
    searchControlServices = new FormControl('');

    // Configuration Object
    private sectionConfig = {
        inventory: {
            search: () => this.searchControl.value,
            type: 'item',
        },
        template: {
            search: () => this.searchControlTemplates.value,
            type: 'template',
        },
        service: {
            search: () => this.searchControlServices.value,
            type: 'service',
        },
    };

    /** Poda de los masivos de canales que ya no existen (ver el constructor). */
    private readonly channelsPruning: Subscription;

    constructor(private http: HttpClient, private toastService: ToastService) {
        // Varios flujos emiten los canales (alta, edición y borrado desde la hoja, login…): podar acá cubre todos. El
        // servicio se suscribe antes que cualquier pantalla, así que cuando la pantalla recalcula la barra ya está podado.
        this.channelsPruning = this.salesChannels$.subscribe(channels => {
            if (Array.isArray(channels)) this.pruneBulkToggles(channels.map(sc => sc._id));
        });
    }

    /** Angular lo llama al destruir el inyector que creó el servicio: con `providedIn: 'root'`, solo al cerrar la app. */
    ngOnDestroy(): void {
        this.channelsPruning.unsubscribe();
    }

    get itemsToUpdate$() {
        return this._itemsToUpdate$;
    }

    get templatesToUpdate$() {
        return this._templatesToUpdate$;
    }

    set itemsToUpdate(items: any[]) {
        this._itemsToUpdate$.next(items);
    }

    set templatesToUpdate(templates: any[]) {
        this._templatesToUpdate$.next(templates);
    }

    loadSalesChannelsInfo(): void {
        this.loading$.next(true);
        this.getSummarizedSalesChannel$().subscribe({
            next: (res: any) => {
                this.salesChannels$.next([...res.data]);
                this.loading$.next(false);
            },
            error: (err: any) => {
                this.toastService.error(err.error.title, err.error.message);
                this.loading$.next(false);
            },
        });
    }

    getSummarizedSalesChannel$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/pos/summarized-sales-channel`);
    }

    /**
     * Recarga la lista de una sección desde la primera tanda.
     *
     * Es el punto de entrada de todo lo que necesita la lista desde cero: entrar a la pantalla, cambiar de pestaña,
     * buscar, y los flujos externos que modifican el catálogo (modificación masiva, carga masiva, alta y edición de
     * canal). Descarta los cambios sin guardar, los masivos pendientes y el índice, como siempre lo hizo.
     */
    getProductsForSalesChannels(section: string) {
        const config = this.sectionConfig[section as keyof typeof this.sectionConfig];

        if (!config) {
            console.error('Sección desconocida en getProductsForSalesChannels:', section);
            return;
        }

        const generation = ++this.listGeneration;
        this.cancelListRequest();
        this.loadingMore$.next(false);
        this.loadMoreFailed$.next(false);

        // Sin esto la tabla se queda mostrando los datos anteriores durante toda la petición, sin
        // ninguna señal de que está recargando. Tras una modificación masiva eso se lee como que
        // el cambio no se aplicó.
        this.loading$.next(true);
        this.listLoading$.next(true);

        const query = { section, type: config.type, search: (config.search() || '').trim() };

        this.listRequest = this.fetchPage(query.type, query.search, 1).subscribe({
            next: res => {
                if (generation !== this.listGeneration) return;

                const products: ProductForSalesChannels[] = this.uniqueById(res?.products || []);
                this.list = { ...query, page: 1, totalPages: Number(res?.totalPages) || 1, totalDocs: Number(res?.totalDocs) || 0 };
                this.originalsById = new Map(products.map(product => [product._id, cloneProduct(product)]));
                this.products = products;
                this.productsToUpdate = [];
                this.channelsStateCache = res?.channelsState ? { section, state: res.channelsState } : null;
                this.channelsCountCache = res?.channelsCount ? { section, counts: res.channelsCount } : null;
                // Una recarga puede venir de otro flujo que modificó el catálogo (carga masiva,
                // modificación masiva de precios, alta de canal): el índice cacheado quedaría
                // desactualizado y un masivo posterior escribiría sobre datos viejos.
                this.invalidateChannelIndex();
                this.loading$.next(false);
                this.listLoading$.next(false);
                this.productsLoaded$.next();
            },
            error: (err: any) => {
                if (generation !== this.listGeneration) return;
                // Sin cuerpo (corte de red o tiempo agotado) el toast saldría vacío.
                this.toastService.error(err?.error?.title ?? 'Error al cargar los productos', err?.error?.message ?? 'No se pudieron cargar los productos. Verifica tu conexión e intenta de nuevo.');
                this.loading$.next(false);
                this.listLoading$.next(false);
            },
        });
    }

    /**
     * Anexa la tanda siguiente de la lista cargada. Nunca toca los cambios sin guardar, los masivos pendientes ni el
     * índice: por eso la carga progresiva conserva lo editado.
     */
    loadMoreProducts(section: string): void {
        const list = this.list;
        if (!list || list.section !== section) return;
        if (this.listLoading$.value || this.loadingMore$.value) return;
        if (list.page >= list.totalPages) return;

        const generation = this.listGeneration;
        const nextPage = list.page + 1;
        this.loadingMore$.next(true);
        this.loadMoreFailed$.next(false);

        this.listRequest = this.fetchPage(list.type, list.search, nextPage).subscribe({
            next: res => {
                if (generation !== this.listGeneration) return;

                // El servidor ordena sin desempate: una tanda puede repetir productos de otra. Se descartan.
                const loadedIds = new Set<string>(this.products.map(product => product._id));
                const fresh: ProductForSalesChannels[] = this.uniqueById(res?.products || []).filter(product => !loadedIds.has(product._id));

                this.list = {
                    ...list,
                    page: nextPage,
                    totalPages: Number(res?.totalPages) || list.totalPages,
                    totalDocs: res?.totalDocs !== undefined ? Number(res.totalDocs) : list.totalDocs,
                };
                // El original se guarda antes de aplicar los masivos: describe la base, no lo pendiente.
                for (const product of fresh) this.originalsById.set(product._id, cloneProduct(product));
                this.applyPendingBulkToggles(fresh);
                this.products = [...this.products, ...fresh];
                if (res?.channelsState) this.channelsStateCache = { section, state: res.channelsState };
                if (res?.channelsCount) this.channelsCountCache = { section, counts: res.channelsCount };

                this.loadingMore$.next(false);
                this.productsAppended$.next(fresh);
            },
            error: (err: any) => {
                if (generation !== this.listGeneration) return;
                this.loadingMore$.next(false);
                this.loadMoreFailed$.next(true);
                this.toastService.error(err?.error?.title ?? 'Error al cargar más productos', err?.error?.message ?? 'No se pudieron cargar más productos. Verifica tu conexión e intenta de nuevo.');
            },
        });
    }

    /**
     * Vuelve a pedir el estado y los contadores por canal del filtro vigente sin tocar las filas cargadas. Tras
     * guardar, describen la base de ANTES del guardado; con esto el header y «Activar todos» vuelven a la realidad sin
     * reiniciar la lista ni perder la posición.
     */
    refreshChannelsSummary(section: string): void {
        const list = this.list;
        if (!list || list.section !== section) return;

        const generation = this.listGeneration;
        const sequence = ++this.summarySequence;
        this.fetchPage(list.type, list.search, 1, 1).subscribe({
            next: res => {
                if (generation !== this.listGeneration || sequence !== this.summarySequence) return;
                this.channelsStateCache = res?.channelsState ? { section, state: res.channelsState } : null;
                this.channelsCountCache = res?.channelsCount ? { section, counts: res.channelsCount } : null;
                if (res?.totalDocs !== undefined) this.list = { ...this.list, totalDocs: Number(res.totalDocs) };
                this.channelsSummaryChanged$.next();
            },
            error: (err: any) => {
                // Sin resumen fresco el header cae a la página visible: no es motivo para molestar con un error. Queda en
                // la consola para que un fallo repetido de este pedido no pase inadvertido.
                console.warn('[SalesChannelsService] no se pudo refrescar el resumen de canales:', err?.status, err?.message);
            },
        });
    }

    /** Hay más tandas por pedir para la lista cargada de esta sección. */
    hasMoreProducts(section: string): boolean {
        return !!this.list && this.list.section === section && this.list.page < this.list.totalPages;
    }

    /** La lista cargada corresponde a esta sección (y no a la pestaña anterior). */
    isListFor(section: string): boolean {
        return !!this.list && this.list.section === section;
    }

    /** Total del filtro según el servidor, no solo lo cargado. */
    getTotalDocs(section: string): number {
        return this.isListFor(section) ? this.list.totalDocs : 0;
    }

    /** Término con el que se pidió la lista cargada; sin lista, el que tenga el buscador. */
    getAppliedSearch(section: string): string {
        return this.isListFor(section) ? this.list.search : this.getSearchTerm(section).trim();
    }

    /** Contadores por canal del listado, solo si corresponden a la sección pedida. */
    getChannelsCount(section: string): ChannelsCountLike {
        return this.channelsCountCache?.section === section ? this.channelsCountCache.counts : null;
    }

    /** Producto tal como lo trajo el listado, antes de cualquier edición. */
    getOriginalProduct(productId: string): ProductForSalesChannels | undefined {
        return this.originalsById.get(productId);
    }

    /**
     * Tras guardar: lo que está en pantalla pasa a ser el original y no queda nada pendiente.
     *
     * Una tanda en vuelo pudo leerse antes de que el guardado escribiera: anexada, mostraría la base de antes y editar
     * una de sus filas reenviaría esos valores viejos. Se cancela y la pantalla vuelve a pedir la misma página.
     */
    markCurrentAsSaved(): void {
        if (this.loadingMore$.value) {
            this.cancelListRequest();
            this.loadingMore$.next(false);
        }
        for (const product of this.products) this.originalsById.set(product._id, cloneProduct(product));
        this.productsToUpdate = [];
        this.bulkToggles.clear();
    }

    /** Devuelve las filas a su original, en el mismo orden (lista nueva, para que la pantalla se repinte). */
    restoreOriginals(): void {
        this.products = this.products.map(product => {
            const original = this.originalsById.get(product._id);
            return original ? cloneProduct(original) : product;
        });
    }

    /** Descarta todo lo pendiente: ediciones individuales y masivos. */
    discardPendingChanges(): void {
        this.restoreOriginals();
        this.productsToUpdate = [];
        this.bulkToggles.clear();
    }

    /**
     * Al salir de la pantalla: nada pendiente queda vivo en el servicio (otras pantallas, como el detalle de precios
     * de un producto, envían `productsToUpdate` y los masivos con el mismo método) y una respuesta tardía no se aplica.
     */
    releaseScreenState(): void {
        this.listGeneration++;
        this.cancelListRequest();
        this.productsToUpdate = [];
        this.bulkToggles.clear();
        this.loadingMore$.next(false);
        this.listLoading$.next(false);
    }

    getProductsSalesChannels({ currentPage = 0, pageSize = 1, search = '', type = 'item' }: ProductsSalesChannelsParams): Observable<any> {
        const params = new HttpParams().set('currentPage', currentPage).set('pageSize', pageSize).set('search', search.trim()).set('type', type);

        return this.http.get<any>(`${this.API_URL}/pos/sales-channel/products`, { params });
    }

    getAllProductsForSalesChannels$(section: string): Observable<any> {
        const config = this.sectionConfig[section as keyof typeof this.sectionConfig];

        if (!config) {
            console.error('Sección desconocida en getAllProductsForSalesChannels$:', section);
            return new Observable(obs => obs.error(new Error('Sección no válida')));
        }

        const type = config.type;

        let params = new HttpParams().set('currentPage', 1).set('pageSize', -1).set('search', '').set('type', type);
        return this.http.get<any>(`${this.API_URL}/pos/sales-channel/products`, { params });
    }

    /**
     * Índice ligero de los productos que matchean el filtro vigente (`_id` + canales habilitados).
     * Alimenta la selección masiva por canal y el estado de los checkbox de header. Se pide una sola
     * vez por (sección + búsqueda) y después se sirve del caché.
     */
    loadChannelIndex$(section: string): Observable<ChannelIndex> {
        const cached = this.getCachedChannelIndex(section);
        if (cached) return of(cached);

        const config = (this.sectionConfig as any)[section];
        if (!config) return new Observable<ChannelIndex>(obs => obs.error(new Error('Sección no válida')));

        const key = this.buildChannelIndexKey(section);
        const generation = this.listGeneration;
        const params = new HttpParams().set('type', config.type).set('search', this.getAppliedSearch(section));

        return this.http.get<any>(`${this.API_URL}/pos/sales-channel/products-index`, { params }).pipe(
            map((res: any) => this.normalizeChannelIndex(res?.data)),
            // Un índice pedido antes de una recarga describe el catálogo de antes: se entrega, pero no se cachea.
            tap(index => {
                if (generation === this.listGeneration) this.channelIndexCache = { key, index };
            }),
        );
    }

    /** Estado por canal del listado, solo si corresponde a la sección pedida; si no, null. */
    getChannelsState(section: string): Record<string, ChannelState> {
        return this.channelsStateCache?.section === section ? this.channelsStateCache.state : null;
    }

    /** Índice cacheado si corresponde al filtro vigente; si no, null. */
    getCachedChannelIndex(section: string): ChannelIndex {
        const key = this.buildChannelIndexKey(section);
        return this.channelIndexCache?.key === key ? this.channelIndexCache.index : null;
    }

    invalidateChannelIndex(): void {
        this.channelIndexCache = null;
        this.bulkToggles.clear();
    }

    /**
     * Descarta el estado y los contadores por canal que devolvió el listado. Hay que llamarlo cuando el
     * catálogo cambia en el backend: describen la base tal como estaba en el último GET, y usarlos después
     * de guardar pinta el header con el estado anterior al guardado.
     */
    invalidateChannelsState(): void {
        this.channelsStateCache = null;
        this.channelsCountCache = null;
    }

    /**
     * Reemplaza el estado y los contadores por canal por los que la pantalla ya calculó para lo recién guardado. Valen
     * hasta que llegue `refreshChannelsSummary`: sin ellos, el header y «Activar todos» quedan sin dato mientras tanto.
     */
    setProvisionalChannelsSummary(section: string, counts: ChannelsCountLike | null, state: Record<string, ChannelState> | null): void {
        this.summarySequence++;
        this.channelsCountCache = counts ? { section, counts } : null;
        this.channelsStateCache = state ? { section, state } : null;
    }

    get pendingBulkToggles(): BulkChannelToggle[] {
        return [...this.bulkToggles.values()];
    }

    /** Registra el masivo de un canal. Con `productIds` vacío no hay nada que escribir: se descarta. */
    setBulkToggle(toggle: BulkChannelToggle): void {
        if (!toggle.productIds.length) this.bulkToggles.delete(toggle.salesChannelId);
        else this.bulkToggles.set(toggle.salesChannelId, toggle);
    }

    clearBulkToggles(): void {
        this.bulkToggles.clear();
    }

    /**
     * Descarta los masivos pendientes de canales que ya no existen. Eliminar un canal desde la hoja solo recarga los
     * canales: tras «Activar todos» su masivo seguía contándose en la barra y viajando al guardar, y la API le volvía a
     * crear la entrada en todos los productos del filtro. Lo llama la suscripción a `salesChannels$` (ver el constructor).
     */
    pruneBulkToggles(channelIds: string[]): void {
        const live = new Set<string>(channelIds);
        for (const channelId of [...this.bulkToggles.keys()]) {
            if (!live.has(channelId)) this.bulkToggles.delete(channelId);
        }
    }

    /**
     * Saca un producto de los masivos pendientes: desde que el usuario lo edita a mano, su estado lo
     * define su propio registro y no el masivo.
     *
     * Sin esto se pierde el caso en que la edición individual deshace justo lo que hizo el masivo: el
     * producto vuelve a coincidir con el original, sale de `productsToUpdate` por no tener cambios, y
     * el masivo lo sobrescribiría de todas formas al guardar.
     *
     * Los canales de un mismo masivo comparten su lista de ids: se filtra una sola vez y la siguen compartiendo, así
     * siguen viajando juntos al guardar y la pantalla no rehace un conjunto por canal.
     */
    excludeFromBulkToggles(productId: string): void {
        const filtered = new Map<string[], string[]>();
        for (const [channelId, toggle] of this.bulkToggles) {
            if (!filtered.has(toggle.productIds)) {
                filtered.set(toggle.productIds, toggle.productIds.includes(productId) ? toggle.productIds.filter(id => id !== productId) : toggle.productIds);
            }
            const productIds = filtered.get(toggle.productIds);
            if (productIds === toggle.productIds) continue;

            if (productIds.length) this.bulkToggles.set(channelId, { ...toggle, productIds });
            else this.bulkToggles.delete(channelId);
        }
    }

    /** Hay algo que guardar: una edición individual o un masivo pendiente. */
    get hasPendingChanges(): boolean {
        return this.productsToUpdate.length > 0 || this.bulkToggles.size > 0;
    }

    onChangeSearchItem(value: string) {
        const items = this.items$.getValue();
        const itemsSc: any = [];
        if (value === '') {
            this.items$.next(items.map(item => ({ ...item, searched: true })));
            this.salesChannels$.getValue().forEach(sc => {
                itemsSc.push({ ...sc, items: sc.items.map((item: any) => ({ ...item, searched: true })) });
            });
        } else {
            const filteredItems = items.map(item => {
                const match = item.name?.toLowerCase().includes(value.toLowerCase()) || item.SKU?.toLowerCase().includes(value.toLowerCase());
                return { ...item, searched: match };
            });
            this.items$.next(filteredItems);
            this.salesChannels$.getValue().forEach(sc => {
                const filteredItemsSC = sc.items.map((item: any) => {
                    const match = item.name?.toLowerCase().includes(value.toLowerCase()) || item.SKU?.toLowerCase().includes(value.toLowerCase());
                    return { ...item, searched: match };
                });
                itemsSc.push({ ...sc, items: filteredItemsSC });
            });
            this.salesChannels$.next(itemsSc);
        }
    }

    onChangeSearchTemplate(value: string) {
        const items = this.templates$.getValue();
        const itemsSc: any = [];
        if (value === '') {
            this.templates$.next(items.map(item => ({ ...item, searched: true })));
            this.salesChannels$.getValue().forEach(sc => {
                itemsSc.push({ ...sc, items: sc.items.map((item: any) => ({ ...item, searched: true })) });
            });
        } else {
            const filteredItems = items.map(item => {
                const match = item.name?.toLowerCase().includes(value.toLowerCase()) || item.SKU?.toLowerCase().includes(value.toLowerCase());
                return { ...item, searched: match };
            });
            this.templates$.next(filteredItems);

            const itemsSc: any = [];
            this.salesChannels$.getValue().forEach(sc => {
                const filteredItemsSC = sc.items.filter((item: any) => item.name?.toLowerCase().includes(value.toLowerCase()));
                itemsSc.push({ ...sc, items: filteredItemsSC });
            });

            this.salesChannels$.next(itemsSc);
        }
    }

    updateItemsInSalesChannels(itemsToUpdate: any[]): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/pos/sales-channel/update-items`, { itemsToUpdate });
    }

    updateTemplatesInSalesChannels(templatesToUpdate: any[]): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/pos/sales-channel/update-templates`, { templatesToUpdate });
    }

    updateProductsForSalesChannels$(): Observable<any> {
        // Respaldo de `pruneBulkToggles`: aunque un masivo de un canal que ya no está siga en memoria, no viaja. Sin
        // canales cargados no hay contra qué comparar y viajan todos, como siempre.
        const channels: any[] = this.salesChannels$.getValue();
        const liveChannelIds = Array.isArray(channels) ? new Set<string>(channels.map(sc => sc._id)) : null;
        const bulkToggles: BulkTogglePayload[] = groupBulkToggles(this.pendingBulkToggles, liveChannelIds);
        const productsToUpdate = liveChannelIds ? this.productsToUpdate.map(product => withoutRemovedChannels(product, liveChannelIds)) : this.productsToUpdate;
        const body: any = { productsToUpdate };
        if (bulkToggles.length > 0) body.bulkToggles = bulkToggles;

        return this.http.post<any>(`${this.API_URL}/pos/sales-channel/update-products`, body);
    }

    bulkUpdatePrices(bulkUpdateData: BulkUpdateData): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/pos/sales-channels/bulk-price-update-products`, { bulkUpdateData });
    }

    /**
     * Devuelve las categorías únicas (string) de los productos activos
     * que pertenecen a los canales y tipos de producto indicados.
     * @param channelIds IDs de los canales de venta seleccionados.
     * @param productTypes Tipos de producto en nomenclatura de BD ('item' | 'template' | 'service').
     */
    getCategoriesByChannels(channelIds: string[], productTypes: string[]): Observable<string[]> {
        return this.http.post<any>(`${this.API_URL}/products/categories-by-channels`, { channelIds, productTypes }).pipe(map((res: any) => (res.data as { name: string }[]).map(c => c.name)));
    }

    createBulkSC$ = (productsToUpdate: any, salesChannels: string[]): Observable<any> => {
        return this.http.post<any>(`${this.API_URL}/pos/sales-channel/bulk-upload`, { productsToUpdate, salesChannels });
    };

    setAllSalesChannelsData(data: any) {
        this.salesChannels$.next([...data.salesChannels]);
        this.SCCopy$.next(JSON.stringify([...data.salesChannels]));
        this.editedSalesChannels$.next(JSON.stringify([...data.salesChannels]));
        this.salesChannelsLoaded$.next(JSON.stringify([...data.salesChannels]));
        this.items$.next([...data.items]);
        this.templates$.next([...data.templates]);

        const items = [...this.items$.getValue()];
        const itemsTemplate = [...this.templates$.getValue()];
        const itemsSc: any = [];
        this.items$.next(items.map(item => ({ ...item, searched: true })));
        this.templates$.next(itemsTemplate.map(item => ({ ...item, searched: true })));
        this.salesChannels$.getValue().forEach(sc => {
            itemsSc.push({ ...sc, items: sc.items.map((item: any) => ({ ...item, searched: true })) });
        });
    }

    /** Buscar vuelve al inicio de la lista con el término nuevo. */
    search(section: string) {
        this.getProductsForSalesChannels(section);
    }

    private fetchPage(type: string, search: string, currentPage: number, pageSize: number = PAGE_SIZE): Observable<any> {
        const params = new HttpParams().set('currentPage', currentPage).set('pageSize', pageSize).set('search', search).set('type', type);
        return this.http.get<any>(`${this.API_URL}/pos/sales-channel/products`, { params });
    }

    private cancelListRequest(): void {
        if (this.listRequest) this.listRequest.unsubscribe();
        this.listRequest = null;
    }

    /** Una misma respuesta tampoco debería traer repetidos, pero si los trae no deben duplicar filas. */
    private uniqueById(products: ProductForSalesChannels[]): ProductForSalesChannels[] {
        const seen = new Set<string>();
        return products.filter(product => {
            if (seen.has(product._id)) return false;
            seen.add(product._id);
            return true;
        });
    }

    /**
     * Las filas nuevas llegan con su estado de la base; si el usuario ya marcó o desmarcó un canal para todo el
     * filtro, ese masivo pendiente también las alcanza y deben verse así.
     */
    private applyPendingBulkToggles(rows: ProductForSalesChannels[]): void {
        for (const toggle of this.bulkToggles.values()) {
            const ids = new Set<string>(toggle.productIds);
            for (const row of rows) {
                if (ids.has(row._id)) setChannelValue(row as any, this.originalsById.get(row._id) as any, toggle.salesChannelId, toggle.enabled);
            }
        }
    }

    private getSearchTerm(section: string): string {
        const term: string = section === 'inventory' ? this.searchControl.value : section === 'template' ? this.searchControlTemplates.value : this.searchControlServices.value;
        return term || '';
    }

    private buildChannelIndexKey(section: string): string {
        return `${section}|${this.getAppliedSearch(section).toLowerCase()}`;
    }

    /**
     * Pasa la respuesta del endpoint a la forma que consume la pantalla. Se normaliza una sola vez,
     * al cachear, para que el recálculo del estado de los headers no tenga que recorrer arrays.
     */
    private normalizeChannelIndex(data: any): ChannelIndex {
        const rawProducts: any[] = data?.products || [];
        const enabledByProduct = new Map<string, Set<string>>();

        for (const product of rawProducts) {
            const enabledChannels = new Set<string>();
            for (const channel of product.channels || []) {
                if (channel.enabled) enabledChannels.add(String(channel.id));
            }
            enabledByProduct.set(product._id, enabledChannels);
        }

        return {
            productIds: rawProducts.map(product => product._id),
            enabledByProduct,
            totalDocs: data?.totalDocs || 0,
            truncated: !!data?.truncated,
        };
    }
}
